import { ColumnDef } from '@tanstack/react-table'
import { OverflownText } from 'components/OverflownText'
import { useMemo, useState } from 'react'
import { Spinner } from 'react-bootstrap'
import { PiPlus, PiTrash } from 'react-icons/pi'
import { Control, useForm } from 'react-hook-form'

import { Dialog, DialogContent } from 'components/UI/Modal/ModalPrimitive'
import { ActionsDropdown } from 'components/Buttons/ActionsDropdown'
import { ClientDataTable } from 'components/ClientDataTable/ClientDataTable'
import { DataTableContainer } from 'components/DataTable/UI'
import { DateCell } from 'components/Tables/CommonTableCells/DateCell'
import { DescriptionCell } from 'components/Tables/CommonTableCells/DescriptionCell'
import { TableLinkStyleProvider } from 'components/Tables/CommonTableCells/TableLinkStyleContext'
import { Modal } from 'components/UI/Modal/Modal'
import useApiService from 'services/api'
import FbButton from 'components/FbUI/FbButton'
import { TextPill } from 'components/FbUI/TextPill'
import {
  CompanyEmailExtensionRequests,
  CompanyEmailExtensionRequestsStatus,
  CompanyEmailExtensionRequestsStatusMap,
} from 'models/companies'
import { TextareaInput } from 'components/FormUtils/TextareaInput'
import { TextInput } from 'components/FormUtils/TextInput'

import { toast } from 'react-toastify'

const DESCRIPTION_TEXT = 'All domains associated with the company.'

// DS2026 50/700 palette — matches Rebate / Sample status pill colors.
const STATUS_PILL_DS2026: Record<
  CompanyEmailExtensionRequestsStatus,
  { bg: string; text: string }
> = {
  [CompanyEmailExtensionRequestsStatus.APPROVED]: {
    bg: '#dcfce7',
    text: '#15803d',
  },
  [CompanyEmailExtensionRequestsStatus.CANCELED]: {
    bg: '#fef3c7',
    text: '#b45309',
  },
  [CompanyEmailExtensionRequestsStatus.DENIED]: {
    bg: '#fee2e2',
    text: '#b91c1c',
  },
  [CompanyEmailExtensionRequestsStatus.PENDING]: {
    bg: '#e0f2fe',
    text: '#0369a1',
  },
}

export function CompanyDomainsTable({ disabled }: { disabled?: boolean }) {
  const api = useApiService()
  const { data, isLoading, refetch, isFetching } =
    api.useGetCompanyDomainRequests()
  const [domainIdToDelete, setDomainIdToDelete] = useState<number>()
  const [showModal, setShowModal] = useState<boolean>(false)

  const handleRowDelete = async () => {
    if (!domainIdToDelete) return

    try {
      await api.cancelCompanyDomainRequest(domainIdToDelete)
      setDomainIdToDelete(undefined)
      toast.success('Request deleted successfully')
    } catch {
      return
    }

    await refetch()
  }

  const columns = useMemo<ColumnDef<CompanyEmailExtensionRequests, any>[]>(
    () => [
      {
        header: 'Domain',
        size: 250,
        meta: { isFirstColumn: true },
        cell: ({ row }) => {
          return <span className="italic">{row.original.email_extension}</span>
        },
      },
      {
        accessorKey: 'status',
        header: 'Status',
        size: 140,
        cell: ({ row }) => {
          const status = row.original.status
          const label = status
            ? CompanyEmailExtensionRequestsStatusMap[status]
            : 'Unavailable'
          const palette =
            STATUS_PILL_DS2026[status] ??
            STATUS_PILL_DS2026[CompanyEmailExtensionRequestsStatus.PENDING]
          const pill = (
            <TextPill backgroundColor={palette.bg} textColor={palette.text}>
              {label}
            </TextPill>
          )
          if (status === CompanyEmailExtensionRequestsStatus.DENIED) {
            return (
              <OverflownText tooltipText={row.original.admin_reason ?? ''}>
                {pill}
              </OverflownText>
            )
          }
          return pill
        },
      },
      {
        accessorKey: 'note',
        header: 'Note',
        size: 600,
        cell: ({ row }) => <DescriptionCell value={row.original.note} />,
      },
      {
        accessorKey: 'created',
        header: 'Date Created',
        size: 160,
        cell: DateCell,
      },
      {
        accessorKey: 'modified',
        header: 'Date Modified',
        size: 160,
        cell: DateCell,
      },
      {
        id: 'actions',
        header: () => <div className="w-full text-center">Actions</div>,
        size: 70,
        minSize: 70,
        maxSize: 70,
        meta: {
          isLastColumn: true,
          headerDisableOrdering: true,
        },
        cell: ({ row }) => {
          const isNotPending =
            row.original.status !== CompanyEmailExtensionRequestsStatus.PENDING
          const isActionDisabled = disabled || isNotPending
          const disabledReason = disabled
            ? 'You do not have permission to delete domain requests'
            : isNotPending
              ? 'You can only delete a pending request'
              : undefined
          return (
            <ActionsDropdown
              className="justify-center"
              actions={[
                {
                  label: 'Delete',
                  icon: PiTrash,
                  variant: 'destructive',
                  disabled: isActionDisabled,
                  disabledReason,
                  onClick: () => setDomainIdToDelete(row.original.id),
                },
              ]}
            />
          )
        },
      },
    ],
    [disabled]
  )

  const { control, setError, handleSubmit, reset } =
    useForm<CompanyEmailExtensionRequestsForm>({
      defaultValues: {
        email_extension: '',
        note: '',
      },
      mode: 'onSubmit',
    })

  const onSubmitCreate = handleSubmit(
    async (
      data: Pick<CompanyEmailExtensionRequests, 'email_extension' | 'note'>
    ) => {
      try {
        await api.postCompanyDomain(data)
      } catch (err: any) {
        if (err.response) {
          setError('email_extension', {
            message: err.response.data.email_extension,
          })
        }
        return
      }

      await refetch()
      setShowModal(false)
      reset()
    }
  )

  if (isLoading || !data || isFetching) {
    return (
      <div className="flex items-center justify-center h-[500px]">
        <Spinner animation="border" />
      </div>
    )
  }

  return (
    <TableLinkStyleProvider variant="plain">
      <div className="flex flex-col w-full flex-1 min-h-0 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-[0px_1px_3px_0px_rgba(16,24,40,0.08),0px_1px_2px_-1px_rgba(16,24,40,0.04)]">
        <DataTableContainer className="h-full">
          <ClientDataTable
            height="100%"
            tableHeader={
              <div
                data-table-toolbar
                className="flex flex-row justify-between items-center gap-3 px-4 py-3"
              >
                <p className="text-sm text-gray-500">{DESCRIPTION_TEXT}</p>
                <FbButton
                  variant="primary"
                  size="sm"
                  onClick={() => setShowModal(true)}
                  disabled={disabled}
                  className="!bg-brand-bright !text-white hover:!bg-brand-active"
                >
                  <PiPlus size={16} />
                  Add Domain
                </FbButton>
              </div>
            }
            data={data || []}
            columns={columns}
            stickyLastColumn
            enableColumnResizing
            rowHeight={44}
            headerHeight={40}
            tableKey="settings-company-domains-table"
          />
        </DataTableContainer>
      </div>
      <Modal
        open={!!domainIdToDelete}
        title={'Delete domain'}
        onOpenChange={(open) => !open && setDomainIdToDelete(undefined)}
        onAccept={() => handleRowDelete()}
        acceptButtonText={'Confirm'}
      >
        <span>Are you sure you want to delete this domain request?</span>
      </Modal>
      <DomainModal
        show={showModal}
        handleClose={() => {
          setShowModal(false)
          reset()
        }}
        control={control}
        onSubmit={onSubmitCreate}
      />
    </TableLinkStyleProvider>
  )
}

type CompanyEmailExtensionRequestsForm = Pick<
  CompanyEmailExtensionRequests,
  'email_extension' | 'note'
>

export function DomainModal({
  show,
  control,
  handleClose,
  onSubmit,
}: {
  show: boolean
  handleClose: () => void
  control: Control<CompanyEmailExtensionRequestsForm, any>
  onSubmit: (e?: React.BaseSyntheticEvent) => Promise<void>
}) {
  return (
    <Dialog open={show} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent id={'fb-modal-content'} className="px-4 max-w-sm">
        <h1 className="mb-4 text-2xl text-gray-800 font-semibold">
          Adding new domain
        </h1>
        <form className="max-w-md mx-auto w-full" onSubmit={onSubmit}>
          <div className="flex flex-col gap-3">
            <div>
              <TextInput
                noMargin
                name="email_extension"
                control={control}
                type="text"
                label="Domain"
                placeholder="domain.com"
                required
              />
            </div>
            <div>
              <TextareaInput
                className="mt-0"
                name="note"
                control={control}
                type="text"
                label="Note"
                placeholder="Add some note (optional)"
              />
            </div>
            <div className="flex gap-3">
              <FbButton
                className="flex-1"
                variant="secondary"
                onClick={handleClose}
              >
                Cancel
              </FbButton>
              <FbButton
                className="flex-1 !bg-brand-bright !text-white hover:!bg-brand-active"
                variant="primary"
                type="submit"
              >
                Add
              </FbButton>
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
